// 13x13 hand category matrix

import { RANKS, type Rank } from './cards.js';

export type HandCategory = string; // e.g. "AKs", "AKo", "AA"

export interface MatrixCell {
  row: number;    // 0-12 (A-2)
  col: number;    // 0-12 (A-2)
  category: HandCategory;
  isPair: boolean;
  isSuited: boolean;
  isOffsuit: boolean;
}

// Build the full 169-cell matrix
// Row < Col = suited (above diagonal), Row > Col = offsuit (below diagonal), Row == Col = pair
export const MATRIX_CELLS: MatrixCell[] = (() => {
  const cells: MatrixCell[] = [];
  for (let row = 0; row < 13; row++) {
    for (let col = 0; col < 13; col++) {
      const r1 = RANKS[row];
      const r2 = RANKS[col];
      let category: HandCategory;
      let isPair = false, isSuited = false, isOffsuit = false;

      if (row === col) {
        category = `${r1}${r1}`;
        isPair = true;
      } else if (row < col) {
        // suited: higher rank first (row is higher because ranks are A..2)
        category = `${r1}${r2}s`;
        isSuited = true;
      } else {
        // offsuit: higher rank first
        category = `${r2}${r1}o`;
        isOffsuit = true;
      }

      cells.push({ row, col, category, isPair, isSuited, isOffsuit });
    }
  }
  return cells;
})();

export const CATEGORY_TO_INDEX: Map<HandCategory, number> = new Map(
  MATRIX_CELLS.map((cell, i) => [cell.category, i])
);

export function getCellIndex(row: number, col: number): number {
  return row * 13 + col;
}

export function getCellByCategory(category: HandCategory): MatrixCell | undefined {
  const idx = CATEGORY_TO_INDEX.get(category);
  return idx !== undefined ? MATRIX_CELLS[idx] : undefined;
}

/** Classify a two-card hand (by rank strings) into a matrix category */
export function classifyHand(r1: Rank, r2: Rank, sameSuit: boolean): HandCategory {
  const i1 = RANKS.indexOf(r1);
  const i2 = RANKS.indexOf(r2);
  if (i1 === i2) return `${r1}${r1}`;
  // Lower index = higher rank
  const high = i1 < i2 ? r1 : r2;
  const low = i1 < i2 ? r2 : r1;
  if (sameSuit) return `${high}${low}s`;
  return `${high}${low}o`;
}

/**
 * Convert two hole cards (e.g. "Kc Qh" or ["Kc","Qh"]) to a hand category (e.g. "KQo").
 * Returns null if the input is invalid.
 */
export function holeCardsToCategory(holeCards: string): HandCategory | null {
  const parts = holeCards.trim().split(/\s+/)
  if (parts.length !== 2) return null

  const [c1, c2] = parts
  if (!c1 || !c2 || c1.length < 2 || c2.length < 2) return null

  const rank1 = c1.slice(0, -1).toUpperCase()
  const suit1 = c1.slice(-1).toLowerCase()
  const rank2 = c2.slice(0, -1).toUpperCase()
  const suit2 = c2.slice(-1).toLowerCase()

  // Rank ordering: A K Q J T 9 8 7 6 5 4 3 2
  const idx1 = RANKS.indexOf(rank1 as Rank)
  const idx2 = RANKS.indexOf(rank2 as Rank)

  if (idx1 === -1 || idx2 === -1) return null

  // Pocket pair
  if (rank1 === rank2) return `${rank1}${rank2}` as HandCategory

  // Higher rank first (lower index = higher rank)
  const [hi, lo] = idx1 < idx2 ? [rank1, rank2] : [rank2, rank1]
  const suited = suit1 === suit2

  return `${hi}${lo}${suited ? 's' : 'o'}` as HandCategory
}
